import {
  NotificationsPage,
  NotificationsProvider,
  notificationsQuery,
  notificationsSearchSchema,
  toNotificationFilters,
  unreadCountQuery,
} from '@smartsash/notifications';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';

import { useAdminNotificationsConfig } from '@/services/notifications/notificationsConfig';

/**
 * HS-1548 (R2-3): the signed-in administrator's own notifications, the portal's screen from
 * `@smartsash/notifications` with admin's translator and links.
 *
 * **Ungated**, like `/dashboard` and `/settings`: every read is `me`-scoped, so `/notifications`
 * requires no permission in `ROUTE_PERMISSIONS`. Authentication is the root route's `beforeLoad`.
 *
 * The filters and the search live in the URL. The first page and the count are prefetched, not
 * awaited: a failing read renders the card's own error with a Retry instead of the route boundary.
 */
export const Route = createFileRoute('/_dashboard/notifications')({
  validateSearch: notificationsSearchSchema,
  loaderDeps: ({ search }) => ({ filters: toNotificationFilters(search) }),
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchInfiniteQuery(notificationsQuery(deps.filters));
    void context.queryClient.prefetchQuery(unreadCountQuery());
  },
  component: NotificationsRoute,
});

function NotificationsRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const config = useAdminNotificationsConfig();

  const onSearchChange = useCallback(
    (q: string | undefined) => {
      void navigate({ search: (previous) => ({ ...previous, q }), replace: true });
    },
    [navigate],
  );

  const onClearFilters = useCallback(() => {
    void navigate({ search: (previous) => ({ ...previous, q: undefined, filters: undefined }) });
  }, [navigate]);

  return (
    <NotificationsProvider value={config}>
      <NotificationsPage
        search={search}
        onSearchChange={onSearchChange}
        onClearFilters={onClearFilters}
      />
    </NotificationsProvider>
  );
}
